export const DEFAULT_MAX_SEARCH_DAYS = 14;

export const DEFAULT_AUDIT_FILTERS = {
  action: "",
  spId: "",
  adminUserId: "",
  from: "",
  to: "",
  page: 1,
  perPage: 25,
};

const toLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getDefaultDateRange = (maxDays = DEFAULT_MAX_SEARCH_DAYS) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - maxDays);

  return {
    from: toLocalDateString(from),
    to: toLocalDateString(to),
  };
};

export const createDefaultAuditFilters = (maxDays = DEFAULT_MAX_SEARCH_DAYS) => ({
  ...DEFAULT_AUDIT_FILTERS,
  ...getDefaultDateRange(maxDays),
});

export const getDaysBetween = (from, to) => {
  if (!from || !to) {
    return 0;
  }

  const start = new Date(from);
  const end = new Date(to);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }

  return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
};

export const validateDateRange = (from, to, maxDays = DEFAULT_MAX_SEARCH_DAYS) => {
  if (!from || !to) {
    return null;
  }

  const start = new Date(from);
  const end = new Date(to);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "Enter valid from and to dates.";
  }

  if (end < start) {
    return "To date must be on or after from date.";
  }

  if (getDaysBetween(from, to) > maxDays) {
    return `Date range cannot exceed ${maxDays} days.`;
  }

  return null;
};

export const hasActiveAuditFilters = (filters, defaults = DEFAULT_AUDIT_FILTERS) =>
  Boolean(
    filters.action ||
      filters.spId ||
      filters.adminUserId ||
      filters.from !== defaults.from ||
      filters.to !== defaults.to
  );

export const buildAuditLogQuery = (filters, basePath = "super-admin/audit-log") => {
  const params = new URLSearchParams();

  if (filters.action) {
    params.append("action", filters.action.trim());
  }

  if (filters.spId) {
    params.append("sp_id", String(filters.spId).trim());
  }

  if (filters.adminUserId) {
    params.append("admin_user_id", String(filters.adminUserId).trim());
  }

  if (filters.from) {
    params.append("from", filters.from);
  }

  if (filters.to) {
    params.append("to", filters.to);
  }

  params.append("page", String(filters.page || 1));
  params.append("per_page", String(filters.perPage || 25));

  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
};

export const buildAuditLogExportQuery = (filters) => {
  const endpoint = buildAuditLogQuery(filters);
  const queryIndex = endpoint.indexOf("?");

  return queryIndex >= 0 ? endpoint.slice(queryIndex + 1) : "";
};

export const formatUserLabel = (user) => {
  if (!user) {
    return "—";
  }

  if (user.name && user.email) {
    return `${user.name} (${user.email})`;
  }

  return user.name || user.email || "—";
};

export const formatTenantLabel = (tenant) => {
  if (!tenant) {
    return "—";
  }

  return tenant.sp_name || tenant.name || "—";
};

export const hasExpandableMetadata = (metadata) =>
  metadata !== null && metadata !== undefined && typeof metadata === "object";

export const formatMetadataJson = (metadata) => {
  if (!hasExpandableMetadata(metadata)) {
    return "{}";
  }

  try {
    return JSON.stringify(metadata, null, 2);
  } catch {
    return String(metadata);
  }
};
